import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../auth/msalConfig';
import { TEAMS_INTERACTIVE_AUTH_KEY, ENTRA_TOKEN_KEY } from '../auth/constants';

function isTopWindow() {
  try {
    return window.self === window.top;
  } catch {
    return true;
  }
}

/**
 * Child of Login: in the Teams auth *popup* (top window), `?bagentTeams=1` + localStorage
 * flag causes an immediate MSAL loginRedirect. Do not use loginPopup in the tab iframe.
 */
export function TeamsLoginSsoStart() {
  const { instance } = useMsal();
  const [searchParams] = useSearchParams();
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    if (searchParams.get('bagentTeams') !== '1') return;
    if (!isTopWindow()) return;
    if (localStorage.getItem(TEAMS_INTERACTIVE_AUTH_KEY) !== '1') return;
    started.current = true;
    instance.loginRedirect(loginRequest).catch(() => {
      started.current = false;
    });
  }, [instance, searchParams]);

  return null;
}

/**
 * Runs only in the Teams *popup* after Entra redirect: passes the id token to the tab via
 * `notifySuccess` so the parent `authentication.authenticate` successCallback receives it.
 */
export function TeamsAuthNotifySuccess() {
  const { instance, accounts } = useMsal();

  useEffect(() => {
    if (localStorage.getItem(TEAMS_INTERACTIVE_AUTH_KEY) !== '1') return;
    if (!isTopWindow()) return;
    if (!accounts || accounts.length === 0) return;

    const account = accounts[0];
    let cancelled = false;

    (async () => {
      try {
        const result = await instance.acquireTokenSilent({ ...loginRequest, account });
        if (cancelled) return;
        const token = result?.idToken;
        if (!token) return;
        if (result?.idToken) localStorage.setItem(ENTRA_TOKEN_KEY, result.idToken);
        const teams = await import('@microsoft/teams-js');
        const { app, authentication } = teams;
        await app.initialize();
        authentication.notifySuccess(token);
        localStorage.removeItem(TEAMS_INTERACTIVE_AUTH_KEY);
      } catch (e) {
        console.error('TeamsAuthNotifySuccess:', e);
        try {
          const teams = await import('@microsoft/teams-js');
          const { app, authentication } = teams;
          await app.initialize();
          authentication.notifyFailure('Could not complete sign-in.');
        } catch {
          // ignore
        }
        try {
          localStorage.removeItem(TEAMS_INTERACTIVE_AUTH_KEY);
        } catch {
          // ignore
        }
      }
    })();

    return () => { cancelled = true; };
  }, [instance, accounts]);

  return null;
}
